import { Inject, Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import * as signalR from '@microsoft/signalr';

/** Payload del evento de fin de carga (coincide con el que emite el backend). */
export interface TransferBulkJobFinished {
    operationGuid: string;
    total: number;
    success: number;
    failed: number;
}

/**
 * Feature 46570 - Cliente SignalR del hub dedicado (/transferBulkHub).
 * Se conecta, se une al grupo del usuario (JoinUser) y expone un stream con el
 * evento 'TransferBulkJobFinished'. Best-effort: si no conecta, el componente
 * puede caer a polling del estado del job.
 */
@Injectable()
export class TransferBulkSignalRService {
    private readonly _urlBase: string;
    private _connection: signalR.HubConnection | null = null;
    private _jobFinished = new Subject<TransferBulkJobFinished>();

    constructor(@Inject('BASE_URL') baseUrl: string) {
        this._urlBase = baseUrl;
    }

    /** Observable que emite cuando un job del usuario termina. */
    get jobFinished$(): Observable<TransferBulkJobFinished> {
        return this._jobFinished.asObservable();
    }

    /** Indica si hay una conexión activa. */
    get isConnected(): boolean {
        return this._connection != null && this._connection.state === signalR.HubConnectionState.Connected;
    }

    /**
     * Inicia la conexión y se une al grupo del usuario. Idempotente: si ya hay
     * conexión activa, solo re-asegura el JoinUser.
     */
    async start(userEmail: string): Promise<void> {
        if (!userEmail) { return; }

        if (this._connection == null) {
            this._connection = new signalR.HubConnectionBuilder()
                // Permite WebSockets y, si el entorno no los habilita (p. ej. App Service con
                // "Web sockets" apagado), cae a Server-Sent Events y Long Polling. Sin esto, el
                // handshake del transporte WebSocket se cancela ("Handshake was canceled") y la
                // conexión nunca se establece. No se usa skipNegotiation para permitir el fallback.
                .withUrl(this._urlBase + 'transferBulkHub', {
                    transport: signalR.HttpTransportType.WebSockets
                             | signalR.HttpTransportType.ServerSentEvents
                             | signalR.HttpTransportType.LongPolling
                })
                .withAutomaticReconnect()
                .build();

            this._connection.on('TransferBulkJobFinished', (payload: TransferBulkJobFinished) => {
                this._jobFinished.next(payload);
            });

            // Al reconectar, re-unirse al grupo del usuario.
            this._connection.onreconnected(() => {
                this._connection?.invoke('JoinUser', userEmail).catch(() => { /* best-effort */ });
            });

            // Traza de cierre inesperado (error de transporte, caída del servidor, etc.).
            this._connection.onclose(err => {
                if (err) {
                    console.warn('[TransferBulkSignalR] Conexión cerrada por error.', err);
                }
            });
        }

        if (this._connection.state === signalR.HubConnectionState.Disconnected) {
            try {
                await this._connection.start();
            } catch (err) {
                // Best-effort: la app no se bloquea si SignalR no conecta (el componente cae a
                // polling). Pero dejamos traza en consola para diagnosticar el transporte
                // (p. ej. "Handshake was canceled" si el App Service corta WebSocket/SSE).
                console.warn('[TransferBulkSignalR] No se pudo conectar al hub; se usará polling.', err);
                throw err;
            }
        }
        await this._connection.invoke('JoinUser', userEmail);
    }

    /** Cierra la conexión (llamar en onDestroy del componente que la usa). */
    async stop(userEmail?: string): Promise<void> {
        if (this._connection == null) { return; }
        try {
            if (userEmail && this.isConnected) {
                await this._connection.invoke('LeaveUser', userEmail);
            }
            await this._connection.stop();
        } catch {
            /* best-effort */
        } finally {
            this._connection = null;
        }
    }
}
